'use client';

import { useEffect, useRef, useState } from 'react';
import { useReducedMotion } from 'motion/react';
import { Mesh, Program, Renderer, Triangle } from 'ogl';

/**
 * Işık huzmeleri — React Bits "Light Rays" (MIT,
 * https://reactbits.dev/backgrounds/light-rays).
 *
 * Şader kaynaktan BİREBİR: bir kaynak noktasından çıkan iki huzme katmanı,
 * açıya göre güç, mesafeye göre sönüm. Kaynak zaten `ogl` kullanıyor, yani
 * kütüphane değişimi yok.
 *
 * Tuval alfalı: huzmenin olmadığı yer saydam, dolayısıyla katman altındaki
 * yüzeyin üstüne normal karışımla oturuyor — `screen` gerekmiyor.
 *
 * ── Kaynaktan sapmalar ────────────────────────────────────────────────────
 *
 * **Azaltılmış hareket.** Kaynakta koşulsuz `requestAnimationFrame` ve fare
 * takibi. MotionConfig sonsuz döngüleri kapatmıyor (DESIGN.md §1), o yüzden
 * elle: tercih açıksa tek kare basılıp duruluyor ve fare dinleyicisi hiç
 * bağlanmıyor. Donmuş huzme eksik görünmüyor — desen zamanın her anında tam.
 *
 * **Görünürlük.** Kaynak yalnızca `IntersectionObserver` ile KURULUMU
 * geciktiriyor, döngüyü durdurmuyor; sekme gizliyken de dönmeye devam ediyor.
 * Burada ikisi de döngüyü durduruyor.
 *
 * **Piksel bütçesi.** Kaynak `dpr` olarak `min(devicePixelRatio, 2)` alıyor;
 * 2560px genişlikte bu 5 Mpx'i geçiyor. Tampon MAX_BUFFER_PIXELS ile
 * sınırlanıyor, huzmeler yumuşak olduğu için CSS büyütmesi görünmüyor.
 *
 * **Ölçü kaynağı.** Kaynak `window.resize` dinliyor; burada kabın kendisi
 * `ResizeObserver` ile izleniyor — panel yüksekliği metin sarınca da değişiyor,
 * pencere ölçüsü değişmeden.
 *
 * **Kendi canvas'ı.** Kaynak kabın çocuklarını silip yenisini ekliyor. Burada
 * her kurulum kendi canvas'ını yaratıp temizlikte kendi canvas'ını götürüyor:
 * bağlamı `loseContext()` ile bıraktığımız için React 19'un geliştirme modunda
 * efekti iki kez çalıştırması aksi hâlde ölümcül olurdu (ikinci kurulum aynı
 * canvas'ın kayıp bağlamını geri alır ve `createProgram()` null döner).
 *
 * **`minWidth` kapısı.** Bu katman yalnızca ≥1200px'te görünen bir panelde
 * kullanılıyor (`.auth-aside` altında `display: none`). Kapı olmasaydı
 * telefonda hiç görünmeyecek bir yüzey için WebGL bağlamı açılırdı. Katmanın
 * kendisi her zaman DOM'da (koşullu render hidrasyonu kırıyor), değişen
 * yalnızca içine tuval kurulup kurulmadığı.
 */

export type RaysOrigin =
  | 'top-center'
  | 'top-left'
  | 'top-right'
  | 'right'
  | 'left'
  | 'bottom-center'
  | 'bottom-right'
  | 'bottom-left';

export type LightRaysProps = {
  className?: string;
  raysOrigin?: RaysOrigin;
  /**
   * Kaynak noktasını `raysOrigin`in koyduğu yerden kaydırıyor — kutunun
   * ölçüsünün kesri olarak, `[x, y]`. Pozitif y aşağı.
   *
   * Kaynakta yok; oradaki sekiz köşe sabit. Işığın kadraja giriş yüksekliği
   * tasarım kararı ve panelden panele değişiyor: `top-center` kaynağı kutunun
   * %20 yukarısına koyuyor, huzmenin en parlak yeri de tepede kalıyor.
   * Ayrı bir kol yerine köşeyi göreli kaydırmak, sekiz köşenin hepsinde aynı
   * anlamı taşıyor.
   */
  raysOffset?: [number, number];
  /**
   * Koninin EKSENİ — `raysOrigin`in getirdiği yönü eziyor. `[x, y]`, pozitif y
   * aşağı. Birim vektör olması gerekmiyor, burada normalize ediliyor.
   *
   * Kaynakta yok: oradaki sekiz köşenin yönü sabit ve hepsi eksene paralel
   * (`top-*` düz aşağı, `left` düz sağa…). Köşeden çapraz vuran bir ışık
   * kaynağın tablosuyla kurulamıyor — `top-right` sağ üst köşeye oturuyor ama
   * yine DÜZ AŞAĞI bakıyor, yelpazenin sola düşen yarısı da yalnızca yayılma
   * açısı kadar sapabiliyor.
   *
   * Verilmezse köşenin kendi yönü kullanılıyor, yani mevcut çağrılar
   * (auth panelleri) etkilenmiyor.
   */
  raysDirection?: [number, number];
  /** Huzmenin rengi (hex). Şaderin çıktısı bununla ÇARPILIYOR. */
  raysColor?: string;
  raysSpeed?: number;
  /** Yayılma açısı. Büyük değer geniş yelpaze, küçük değer dar demet. */
  lightSpread?: number;
  /** Huzme boyu, genişliğin katı olarak. */
  rayLength?: number;
  pulsating?: boolean;
  fadeDistance?: number;
  saturation?: number;
  /** Fare takibi. Azaltılmış hareket tercihinde zaten kapanıyor. */
  followMouse?: boolean;
  mouseInfluence?: number;
  noiseAmount?: number;
  distortion?: number;
  /** Çizim tamponu / CSS ölçüsü oranı. Tavan ayrıca MAX_BUFFER_PIXELS. */
  resolutionScale?: number;
  /** Altında tuval kurulacak en küçük genişlik. Altında katman boş kalır. */
  minWidth?: number;
};

/** 2.2 Mpx ≈ 1920×1150. Huzmeler yumuşak, CSS büyütmesi görünmüyor. */
const MAX_BUFFER_PIXELS = 2_200_000;

/** '#rrggbb' → [r, g, b], 0–1. Çözülemeyen değer beyaza düşüyor. */
const hexToRgb = (hex: string): [number, number, number] => {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!m || !m[1] || !m[2] || !m[3]) return [1, 1, 1];
  return [parseInt(m[1], 16) / 255, parseInt(m[2], 16) / 255, parseInt(m[3], 16) / 255];
};

/* Kaynaktan birebir: kaynak noktası kutunun %20 dışında duruyor ki huzmenin
   tepe noktası kadraja girmesin. */
const getAnchorAndDir = (
  origin: RaysOrigin,
  w: number,
  h: number,
): { anchor: [number, number]; dir: [number, number] } => {
  const outside = 0.2;
  switch (origin) {
    case 'top-left':
      return { anchor: [0, -outside * h], dir: [0, 1] };
    case 'top-right':
      return { anchor: [w, -outside * h], dir: [0, 1] };
    case 'left':
      return { anchor: [-outside * w, 0.5 * h], dir: [1, 0] };
    case 'right':
      return { anchor: [(1 + outside) * w, 0.5 * h], dir: [-1, 0] };
    case 'bottom-left':
      return { anchor: [0, (1 + outside) * h], dir: [0, -1] };
    case 'bottom-center':
      return { anchor: [0.5 * w, (1 + outside) * h], dir: [0, -1] };
    case 'bottom-right':
      return { anchor: [w, (1 + outside) * h], dir: [0, -1] };
    default:
      return { anchor: [0.5 * w, -outside * h], dir: [0, 1] };
  }
};

const vertex = `
attribute vec2 position;
varying vec2 vUv;
void main() {
  vUv = position * 0.5 + 0.5;
  gl_Position = vec4(position, 0.0, 1.0);
}
`;

/* Kaynaktan birebir kopya. */
const fragment = `precision highp float;

uniform float iTime;
uniform vec2  iResolution;

uniform vec2  rayPos;
uniform vec2  rayDir;
uniform vec3  raysColor;
uniform float raysSpeed;
uniform float lightSpread;
uniform float rayLength;
uniform float pulsating;
uniform float fadeDistance;
uniform float saturation;
uniform vec2  mousePos;
uniform float mouseInfluence;
uniform float noiseAmount;
uniform float distortion;

varying vec2 vUv;

float noise(vec2 st) {
  return fract(sin(dot(st.xy, vec2(12.9898,78.233))) * 43758.5453123);
}

float rayStrength(vec2 raySource, vec2 rayRefDirection, vec2 coord,
                  float seedA, float seedB, float speed) {
  vec2 sourceToCoord = coord - raySource;
  vec2 dirNorm = normalize(sourceToCoord);
  float cosAngle = dot(dirNorm, rayRefDirection);

  float distortedAngle = cosAngle + distortion * sin(iTime * 2.0 + length(sourceToCoord) * 0.01) * 0.2;

  float spreadFactor = pow(max(distortedAngle, 0.0), 1.0 / max(lightSpread, 0.001));

  float distance = length(sourceToCoord);
  float maxDistance = iResolution.x * rayLength;
  float lengthFalloff = clamp((maxDistance - distance) / maxDistance, 0.0, 1.0);

  float fadeFalloff = clamp((iResolution.x * fadeDistance - distance) / (iResolution.x * fadeDistance), 0.5, 1.0);
  float pulse = pulsating > 0.5 ? (0.8 + 0.2 * sin(iTime * speed * 3.0)) : 1.0;

  float baseStrength = clamp(
    (0.45 + 0.15 * sin(distortedAngle * seedA + iTime * speed)) +
    (0.3 + 0.2 * cos(-distortedAngle * seedB + iTime * speed)),
    0.0, 1.0
  );

  return baseStrength * lengthFalloff * fadeFalloff * spreadFactor * pulse;
}

void mainImage(out vec4 fragColor, in vec2 fragCoord) {
  vec2 coord = vec2(fragCoord.x, iResolution.y - fragCoord.y);

  vec2 finalRayDir = rayDir;
  if (mouseInfluence > 0.0) {
    vec2 mouseScreenPos = mousePos * iResolution.xy;
    vec2 mouseDirection = normalize(mouseScreenPos - rayPos);
    finalRayDir = normalize(mix(rayDir, mouseDirection, mouseInfluence));
  }

  vec4 rays1 = vec4(1.0) *
               rayStrength(rayPos, finalRayDir, coord, 36.2214, 21.11349,
                           1.5 * raysSpeed);
  vec4 rays2 = vec4(1.0) *
               rayStrength(rayPos, finalRayDir, coord, 22.3991, 18.0234,
                           1.1 * raysSpeed);

  fragColor = rays1 * 0.5 + rays2 * 0.4;

  if (noiseAmount > 0.0) {
    float n = noise(coord * 0.01 + iTime * 0.1);
    fragColor.rgb *= (1.0 - noiseAmount + noiseAmount * n);
  }

  float brightness = 1.0 - (coord.y / iResolution.y);
  fragColor.x *= 0.1 + brightness * 0.8;
  fragColor.y *= 0.3 + brightness * 0.6;
  fragColor.z *= 0.5 + brightness * 0.5;

  if (saturation != 1.0) {
    float gray = dot(fragColor.rgb, vec3(0.299, 0.587, 0.114));
    fragColor.rgb = mix(vec3(gray), fragColor.rgb, saturation);
  }

  fragColor.rgb *= raysColor;
}

void main() {
  vec4 color;
  mainImage(color, gl_FragCoord.xy);
  gl_FragColor = color;
}`;

export function LightRays({
  className,
  raysOrigin = 'top-center',
  raysOffset,
  raysDirection,
  raysColor = '#ffffff',
  raysSpeed = 1,
  lightSpread = 1,
  rayLength = 2,
  pulsating = false,
  fadeDistance = 1.0,
  saturation = 1.0,
  followMouse = true,
  mouseInfluence = 0.1,
  noiseAmount = 0.0,
  distortion = 0.0,
  resolutionScale = 0.9,
  minWidth = 0,
}: LightRaysProps) {
  const mountRef = useRef<HTMLDivElement>(null);
  const reduce = useReducedMotion();

  /* Diziden çıkarılıp sayı olarak tutuluyor: `raysOffset` satır içi bir dizi
     literali olarak geliyor, yani her render'da yeni bir kimlik. Doğrudan
     bağımlılık listesine konsaydı efekt her render'da WebGL bağlamını yıkıp
     yeniden kurardı. */
  const offsetX = raysOffset?.[0] ?? 0;
  const offsetY = raysOffset?.[1] ?? 0;
  /* Aynı gerekçe: `raysDirection` da satır içi dizi literali. */
  const dirX = raysDirection?.[0];
  const dirY = raysDirection?.[1];

  /* Kapı efektten değil state'ten okunuyor: eşik aşıldığında efektin yeniden
     çalışması ve tuvali o anda kurması gerekiyor. */
  const [wide, setWide] = useState(minWidth <= 0);
  useEffect(() => {
    if (minWidth <= 0) return;
    const mq = window.matchMedia(`(min-width: ${minWidth}px)`);
    const sync = () => setWide(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, [minWidth]);

  useEffect(() => {
    const parent = mountRef.current;
    if (!parent || !wide) return;

    let renderer: Renderer;
    try {
      renderer = new Renderer({ alpha: true, depth: false, stencil: false, antialias: false, dpr: 1 });
    } catch {
      /* WebGL kurulamazsa katman boş kalıyor; altındaki yüzey kendi zeminiyle
         duruyor, eksik bir şey görünmüyor. */
      return;
    }

    const gl = renderer.gl;
    const canvas = gl.canvas;
    canvas.style.display = 'block';
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    parent.appendChild(canvas);

    /* Fare yalnızca hareket açıkken izleniyor. */
    const trackMouse = followMouse && mouseInfluence > 0 && !reduce;

    const uniforms = {
      iTime: { value: 0 },
      iResolution: { value: [1, 1] as [number, number] },
      rayPos: { value: [0, 0] as [number, number] },
      rayDir: { value: [0, 1] as [number, number] },
      raysColor: { value: hexToRgb(raysColor) },
      raysSpeed: { value: raysSpeed },
      lightSpread: { value: lightSpread },
      rayLength: { value: rayLength },
      pulsating: { value: pulsating ? 1.0 : 0.0 },
      fadeDistance: { value: fadeDistance },
      saturation: { value: saturation },
      mousePos: { value: [0.5, 0.5] as [number, number] },
      mouseInfluence: { value: trackMouse ? mouseInfluence : 0 },
      noiseAmount: { value: noiseAmount },
      distortion: { value: distortion },
    };

    const program = new Program(gl, { vertex, fragment, uniforms });
    const mesh = new Mesh(gl, { geometry: new Triangle(gl), program });

    /**
     * Kaynak `iResolution`ı ve kaynak noktasını CİHAZ pikseliyle hesaplıyor.
     * Burada da öyle — ama "cihaz pikseli" tampon ölçüsü, çünkü tamponu
     * MAX_BUFFER_PIXELS'e sıkıştırıyoruz. İkisinin aynı birimde olması şart:
     * `rayPos` tamponun dışında bir noktayı işaret ediyor ve `gl_FragCoord` da
     * tampon uzayında.
     */
    const resize = () => {
      const wCSS = Math.max(1, parent.clientWidth);
      const hCSS = Math.max(1, parent.clientHeight);
      const dpr = Math.min(typeof window === 'undefined' ? 1 : window.devicePixelRatio || 1, 2);
      let s = resolutionScale * dpr;
      const area = wCSS * hCSS * s * s;
      if (area > MAX_BUFFER_PIXELS) s *= Math.sqrt(MAX_BUFFER_PIXELS / area);

      const w = Math.max(1, Math.round(wCSS * s));
      const h = Math.max(1, Math.round(hCSS * s));
      renderer.setSize(w, h);
      uniforms.iResolution.value = [w, h];

      const { anchor, dir } = getAnchorAndDir(raysOrigin, w, h);
      uniforms.rayPos.value = [anchor[0] + offsetX * w, anchor[1] + offsetY * h];
      /* Şader `rayDir`i olduğu gibi kullanıyor (yalnızca fare karışımında
         normalize ediyor), o yüzden normalize etmek buraya düşüyor. */
      if (dirX !== undefined && dirY !== undefined) {
        const len = Math.hypot(dirX, dirY) || 1;
        uniforms.rayDir.value = [dirX / len, dirY / len];
      } else {
        uniforms.rayDir.value = dir;
      }

      /* setSize canvas'a piksel ölçüsünü yazıyor; CSS ölçüsünü geri al. */
      canvas.style.width = '100%';
      canvas.style.height = '100%';
    };

    const draw = (seconds: number) => {
      uniforms.iTime.value = seconds;
      renderer.render({ scene: mesh });
    };

    resize();
    const ro = new ResizeObserver(() => {
      resize();
      if (reduce) draw(0);
    });
    ro.observe(parent);

    /* Azaltılmış hareket: döngü yok, fare yok, tek kare. */
    if (reduce) {
      draw(0);
      return () => {
        ro.disconnect();
        canvas.remove();
        gl.getExtension('WEBGL_lose_context')?.loseContext();
      };
    }

    /* Fare hedefi ve yumuşatılmış hâli. Yumuşatma kaynaktan (0.92): imleç
       sıçrayınca huzme onu kovalamıyor, arkasından süzülüyor. */
    const target = { x: 0.5, y: 0.5 };
    const smooth = { x: 0.5, y: 0.5 };
    const onMouseMove = (e: MouseEvent) => {
      const r = parent.getBoundingClientRect();
      if (!r.width || !r.height) return;
      target.x = (e.clientX - r.left) / r.width;
      target.y = (e.clientY - r.top) / r.height;
    };
    if (trackMouse) window.addEventListener('mousemove', onMouseMove, { passive: true });

    const start = performance.now();
    let raf: number | null = null;
    let running = false;
    let visible = true;

    const loop = () => {
      if (!running) return;
      if (trackMouse) {
        const k = 0.92;
        smooth.x = smooth.x * k + target.x * (1 - k);
        smooth.y = smooth.y * k + target.y * (1 - k);
        uniforms.mousePos.value = [smooth.x, smooth.y];
      }
      draw((performance.now() - start) / 1000);
      raf = requestAnimationFrame(loop);
    };
    const play = () => {
      if (running) return;
      running = true;
      loop();
    };
    const stop = () => {
      running = false;
      if (raf !== null) cancelAnimationFrame(raf);
      raf = null;
    };

    const onVisibility = () => {
      if (document.hidden) stop();
      else if (visible) play();
    };
    document.addEventListener('visibilitychange', onVisibility);

    const io = new IntersectionObserver(
      (entries) => {
        visible = !!entries[0]?.isIntersecting;
        if (visible && !document.hidden) play();
        else stop();
      },
      { threshold: [0, 0.01] },
    );
    io.observe(parent);

    play();

    return () => {
      stop();
      io.disconnect();
      ro.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
      if (trackMouse) window.removeEventListener('mousemove', onMouseMove);
      canvas.remove();
      gl.getExtension('WEBGL_lose_context')?.loseContext();
    };
  }, [
    wide,
    raysOrigin,
    offsetX,
    offsetY,
    dirX,
    dirY,
    raysColor,
    raysSpeed,
    lightSpread,
    rayLength,
    pulsating,
    fadeDistance,
    saturation,
    followMouse,
    mouseInfluence,
    noiseAmount,
    distortion,
    resolutionScale,
    reduce,
  ]);

  return <div ref={mountRef} className={className} aria-hidden="true" />;
}
